/**
 * FILE: packages/supabase/src/placements/writes.ts
 * PURPOSE: Placements domain — all write operations.
 * AGENT_CONTEXT: Every write calls logActivity(). Use withActivityActor().
 * INVARIANTS: Phone changes via mergeStudentPhone RPC. No fntr_job_id updates.
 * LAST_REVIEWED: 2026-06-10
 */

import type { DbClient } from '@fintree/supabase/db-client'
import { defaultDbClient } from '@fintree/supabase/db-client'
import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import { ServiceError } from '../internal/errors'
import { callInternalEdgeFunction } from '../internal/edgeFunctionAuth'
import { today, checkEligibility, type MatchLevel } from '@fintree/utils'
import type { Database } from '../types'
import type {
  JobWithDetails,
  RecruiterWithCompany,
  ShortlistDraftRow,
  ShortlistCandidate,
  ResumeZipResult,
  InboxStaffStatus,
  PlacementHistoryRow,
} from './dto'
import {
  getJobById,
  attachJobDetails,
  normaliseApplicationStatus,
  isShortlistedDbStatus,
  isPlacedDbStatus,
  buildShortlistEmailDraft,
} from './reads'

type JobRow = Database['public']['Tables']['jobs']['Row']
type JobInsert = Database['public']['Tables']['jobs']['Insert']
type Company = Database['public']['Tables']['companies']['Row']
type CompanyInsert = Database['public']['Tables']['companies']['Insert']
type Recruiter = Database['public']['Tables']['recruiters']['Row']
type RecruiterInsert = Database['public']['Tables']['recruiters']['Insert']
type Application = Database['public']['Tables']['applications']['Row']
type ApplicationInsert = Database['public']['Tables']['applications']['Insert']
type Shortlist = Database['public']['Tables']['shortlists']['Row']

/* ════════════════════════════════════════════════════════════════════
   Jobs
   ════════════════════════════════════════════════════════════════════ */

async function resolveCompanyTextFromPayload(
  payload: { company?: string | null; company_id?: string | null },
  client: DbClient = defaultDbClient
): Promise<{
  company: string | null
  company_id: string | null
}> {
  const companyId =
    payload.company_id != null && String(payload.company_id).trim() ? String(payload.company_id).trim() : null
  if (companyId) {
    const { data: co, error } = await client.from('companies').select('name').is('deleted_at', null).eq('id', companyId).maybeSingle()
    if (error) throw error
    if (!co?.name?.trim()) throw new Error('Company not found')
    return { company: co.name.trim(), company_id: companyId }
  }
  const text = payload.company?.trim() || null
  if (!text) throw new Error('Company is required (select a company or enter a name)')
  return { company: text, company_id: null }
}

export async function createJob(
  payload: Omit<JobInsert, 'id' | 'fntr_job_id' | 'created_at' | 'updated_at'> & { fntr_job_id?: string },
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<JobWithDetails> {
  const { company, company_id } = await resolveCompanyTextFromPayload(payload, client)

  let fntrJobId = payload.fntr_job_id
  if (!fntrJobId) {
    const { data: generatedId, error: rpcError } = await client.rpc('generate_fntr_job_id')
    if (rpcError) throw rpcError
    if (!generatedId || typeof generatedId !== 'string') throw new Error('generate_fntr_job_id returned no id')
    fntrJobId = generatedId
  }

  const row: JobInsert = {
    ...payload,
    company,
    company_id,
    fntr_job_id: fntrJobId,
    posted_date: payload.posted_date ?? today(),
    status: payload.status ?? 'active',
    posted_by: payload.posted_by ?? actor.userName,
  }

  const { data, error } = await client.from('jobs').insert(row).select('*').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_JOB',
      entityType: 'jobs',
      entityId: data.id,
      newValues: { fntr_job_id: fntrJobId, title: data.title },
    }),
    client
  )

  const [out] = await attachJobDetails([data], client)
  return out!
}

export async function updateJob(
  id: string,
  changes: Partial<Omit<JobRow, 'id' | 'fntr_job_id' | 'created_at'>>,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<JobWithDetails> {
  if ('fntr_job_id' in changes && (changes as { fntr_job_id?: string }).fntr_job_id !== undefined) {
    throw new Error('fntr_job_id cannot be updated')
  }

  const previous = await getJobById(id, client)
  let patch: Partial<JobRow> = { ...changes }

  if (changes.company_id !== undefined || changes.company !== undefined) {
    const merged = {
      company: changes.company !== undefined ? changes.company : previous?.company,
      company_id: changes.company_id !== undefined ? changes.company_id : previous?.company_id,
    }
    const resolved = await resolveCompanyTextFromPayload(merged, client)
    patch = { ...patch, company: resolved.company, company_id: resolved.company_id }
  }

  const { data, error } = await client.from('jobs').update(patch).eq('id', id).select('*').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_JOB',
      entityType: 'jobs',
      entityId: id,
      oldValues: previous ? (JSON.parse(JSON.stringify(previous)) as Record<string, unknown>) : undefined,
      newValues: JSON.parse(JSON.stringify(data)) as Record<string, unknown>,
    }),
    client
  )

  const [out] = await attachJobDetails([data], client)
  return out!
}

export async function closeJob(
  id: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<void> {
  const previous = await getJobById(id, client)
  const { error } = await client.from('jobs').update({ status: 'closed' }).eq('id', id)
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'CLOSE_JOB',
      entityType: 'jobs',
      entityId: id,
      oldValues: previous ? (JSON.parse(JSON.stringify(previous)) as Record<string, unknown>) : undefined,
      newValues: { status: 'closed' },
    }),
    client
  )
}

export async function deleteJob(
  id: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<void> {
  const previous = await getJobById(id, client)
  const now = new Date().toISOString()
  const { error } = await client.from('jobs').update({ deleted_at: now }).eq('id', id)
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'DELETE_JOB',
      entityType: 'jobs',
      entityId: id,
      oldValues: previous ? (JSON.parse(JSON.stringify(previous)) as Record<string, unknown>) : undefined,
      newValues: { deleted_at: now },
    }),
    client
  )
}

export async function setJobVisibility(
  id: string,
  visibleToStudents: boolean,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<JobWithDetails> {
  const previous = await getJobById(id, client)
  const { data, error } = await client
    .from('jobs')
    .update({ visible_to_students: visibleToStudents })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_JOB_VISIBILITY',
      entityType: 'jobs',
      entityId: id,
      oldValues: previous ? { visible_to_students: previous.visible_to_students } : undefined,
      newValues: { visible_to_students: data.visible_to_students },
    }),
    client
  )

  const [out] = await attachJobDetails([data], client)
  return out!
}

export async function duplicateJob(
  sourceJobId: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<JobWithDetails> {
  const source = await getJobById(sourceJobId, client)
  if (!source) throw new ServiceError('Source job not found', 'JOB_NOT_FOUND')

  const newJob = await createJob(
    {
      title: `${source.title} (Copy)`,
      company: source.company,
      company_id: source.company_id,
      location: source.location,
      job_type: source.job_type,
      description: source.description,
      structured_description: source.structured_description,
      requirements: source.requirements,
      course_required: source.course_required,
      exp_band_required: source.exp_band_required,
      salary_min: source.salary_min,
      salary_max: source.salary_max,
      deadline: null, // reset deadline
      status: 'active',
      recruiter_id: source.recruiter_id,
    },
    actor,
    client
  )

  return newJob
}

/* ════════════════════════════════════════════════════════════════════
   Companies
   ════════════════════════════════════════════════════════════════════ */

export async function createCompany(
  payload: { name: string; industry?: string; website?: string; notes?: string },
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<Company> {
  const name = payload.name?.trim()
  if (!name) throw new Error('Company name is required')

  const row: CompanyInsert = {
    name,
    industry: payload.industry?.trim() || null,
    website: payload.website?.trim() || null,
    notes: payload.notes?.trim() || null,
    active: true,
  }

  const { data, error } = await client.from('companies').insert(row).select('*').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_COMPANY',
      entityType: 'companies',
      entityId: data.id,
      newValues: { name: data.name },
    }),
    client
  )

  return data
}

export async function updateCompany(
  id: string,
  changes: Partial<Company>,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<Company> {
  const { data: previous } = await client.from('companies').select('*').is('deleted_at', null).eq('id', id).maybeSingle()
  const { data, error } = await client.from('companies').update(changes).eq('id', id).select('*').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_COMPANY',
      entityType: 'companies',
      entityId: id,
      oldValues: previous ?? undefined,
      newValues: data,
    }),
    client
  )

  return data
}

/* ════════════════════════════════════════════════════════════════════
   Recruiters
   ════════════════════════════════════════════════════════════════════ */

type CompanyEmbed = { id: string; name: string; industry: string | null }

function mapRow(
  row: Recruiter & { companies?: CompanyEmbed | CompanyEmbed[] | null }
): RecruiterWithCompany {
  const raw = row.companies
  const emb = Array.isArray(raw) ? raw[0] : raw
  const company = emb ? { id: emb.id, name: emb.name, industry: emb.industry } : null
  const legacy_company = row.company
  const company_label = company?.name ?? legacy_company ?? null

  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    designation: row.designation,
    company_id: row.company_id,
    company,
    legacy_company,
    company_label,
    industry: row.industry,
    notes: row.notes,
    active: row.active !== false && row.active !== null,
    created_at: row.created_at,
  }
}

export async function createRecruiter(
  payload: {
    name: string
    company_id?: string
    designation?: string
    company?: string
    email?: string
    phone?: string
    industry?: string
    notes?: string
  },
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<RecruiterWithCompany> {
  if (!payload.name?.trim()) throw new Error('Recruiter name is required')

  let companyText: string | null = payload.company?.trim() || null
  let industry: string | null = payload.industry?.trim() || null

  if (payload.company_id) {
    const { data: co, error: ce } = await client.from('companies').select('name,industry').is('deleted_at', null).eq('id', payload.company_id).maybeSingle()
    if (ce) throw ce
    if (!co) throw new Error('Company not found')
    companyText = co.name
    if (!industry && co.industry) industry = co.industry
  }

  const row: RecruiterInsert = {
    name: payload.name.trim(),
    company_id: payload.company_id?.trim() || null,
    designation: payload.designation?.trim() || null,
    company: companyText,
    email: payload.email?.trim() || null,
    phone: payload.phone?.trim() || null,
    industry,
    notes: payload.notes?.trim() || null,
    active: true,
  }

  const { data, error } = await client.from('recruiters').insert(row).select('*, companies(id,name,industry)').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_RECRUITER',
      entityType: 'recruiters',
      entityId: data.id,
      newValues: { name: data.name, company: data.company, company_id: data.company_id },
    }),
    client
  )

  return mapRow(data as Recruiter & { companies?: CompanyEmbed | CompanyEmbed[] | null })
}

export async function updateRecruiter(
  id: string,
  changes: Partial<Recruiter>,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<RecruiterWithCompany> {
  const { data: previous } = await client.from('recruiters').select('*').is('deleted_at', null).eq('id', id).maybeSingle()
  let patch: Partial<Recruiter> = { ...changes }

  if (changes.company_id !== undefined && changes.company_id) {
    const { data: co, error: ce } = await client.from('companies').select('name,industry').is('deleted_at', null).eq('id', changes.company_id).maybeSingle()
    if (ce) throw ce
    if (co) {
      patch = { ...patch, company: co.name }
      if (changes.industry === undefined && co.industry) patch.industry = co.industry
    }
  }

  const { data, error } = await client.from('recruiters').update(patch).eq('id', id).select('*, companies(id,name,industry)').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_RECRUITER',
      entityType: 'recruiters',
      entityId: id,
      oldValues: previous ?? undefined,
      newValues: data,
    }),
    client
  )

  return mapRow(data as Recruiter & { companies?: CompanyEmbed | CompanyEmbed[] | null })
}

/* ════════════════════════════════════════════════════════════════════
   Applications (writes)
   ════════════════════════════════════════════════════════════════════ */

const PLACEMENT_EMAIL_STATUSES = new Set([
  'applied',
  'shortlisted',
  'shortlisted_fintree',
  'sent_to_recruiter',
  'forwarded',
  'interview_scheduled',
  'placed',
  'selected',
  'rejected',
])

function normalizePlacementEmailStatus(status: string): string {
  if (status === 'shortlisted_fintree') return 'shortlisted'
  if (status === 'forwarded') return 'sent_to_recruiter'
  if (status === 'selected') return 'placed'
  return status
}

async function notifyStudentStatusChange(
  phone: string | null | undefined,
  jobId: string | null | undefined,
  oldStatus: string | null | undefined,
  newStatus: string | null | undefined,
  client: DbClient = defaultDbClient,
): Promise<void> {
  if (!phone || !jobId || !newStatus) return
  try {
    const [{ data: job }, { data: application }] = await Promise.all([
      client.from('jobs').select('title,company').is('deleted_at', null).eq('id', jobId).maybeSingle(),
      client
        .from('applications')
        .select('shortlisted_at')
        .eq('phone', phone)
        .eq('job_id', jobId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ])
    const response = await callInternalEdgeFunction('notify-student-status', {
      phone,
      jobTitle: job?.title ?? '',
      company: job?.company ?? '',
      oldStatus: oldStatus ?? '',
      newStatus,
      shortlistedAt: application?.shortlisted_at ?? null,
    })
    if (!response.ok) {
      console.warn(`[notifyStudentStatusChange] Failed for phone=${phone}, job=${jobId}: ${response.status}`)
    }
  } catch (err) {
    console.warn(`[notifyStudentStatusChange] Error for phone=${phone}, job=${jobId}:`, err)
  }
}

async function sendPlacementEmail(applicationId: string, status: string): Promise<void> {
  if (!PLACEMENT_EMAIL_STATUSES.has(status)) return
  try {
    const response = await callInternalEdgeFunction('send-placement-email', {
      applicationId,
      status: normalizePlacementEmailStatus(status),
    })
    if (!response.ok) {
      console.warn(
        `[sendPlacementEmail] Failed for application=${applicationId}, status=${status}: ${response.status}`
      )
    }
  } catch (err) {
    console.warn(`[sendPlacementEmail] Error for application=${applicationId}:`, err)
  }
}

/** Inserts a phone-only application, logs `CREATE_APPLICATION`. */
export async function createApplication(
  payload: {
    jobId: string
    phone: string
    appliedVia?: string
    matchLevel?: string
    notes?: string
  },
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<Application> {
  const phone = payload.phone.trim()
  if (!phone) throw new Error('phone is required')

  const job = await getJobById(payload.jobId, client)
  if (!job) throw new Error('Job not found')
  if (job.status !== 'open' && job.status !== 'active') {
    throw new Error('This opening is no longer accepting applications')
  }

  const { data: dup } = await client.from('applications').select('id').eq('job_id', payload.jobId).eq('phone', phone).is('deleted_at', null).maybeSingle()
  if (dup) throw new Error('Application already exists for this job and phone')

  const [{ data: student }, { data: profile }] = await Promise.all([
    client
      .from('students')
      .select('phone,name,course,attempt,exp_band')
      .eq('phone', phone)
      .is('deleted_at', null)
      .maybeSingle(),
    client
      .from('student_profiles')
      .select('phone,qualification')
      .eq('phone', phone)
      .is('deleted_at', null)
      .maybeSingle(),
  ])

  if (!student) throw new Error('Student not found for this phone. Enroll the student in Hub first.')
  const qualification = profile?.qualification ?? null

  /** Admin callers may supply an explicit match level; otherwise it is calculated. */
  let matchLevel: MatchLevel
  if (payload.matchLevel) {
    matchLevel = payload.matchLevel as MatchLevel
  } else {
    const eligibility = checkEligibility(
      {
        expBandRequired: job.exp_band_required,
        minClearedLevel: job.min_cleared_level,
      },
      {
        expBand: student?.exp_band,
        qualification,
      }
    )
    matchLevel = eligibility.eligible ? 'strong_match' : 'not_eligible'
  }

  const insert: ApplicationInsert = {
    job_id: payload.jobId,
    phone,
    applied_via: payload.appliedVia ?? 'hub',
    match_level: matchLevel,
    status: 'applied',
    notes: payload.notes ?? null,
  }

  const { data, error } = await client.from('applications').insert(insert).select('*').single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_APPLICATION',
      entityType: 'applications',
      entityId: data.id,
      newValues: { job_id: data.job_id, phone: data.phone, match_level: data.match_level },
    }),
    client
  )

  // Fire-and-forget applied confirmation email.
  void sendPlacementEmail(data.id, 'applied')

  return data
}

type StatusExtra = {
  shortlistedBy?: string
  recruiterFeedback?: string
  interviewDate?: string
  packageLpa?: number
  notes?: string
}

function buildApplicationStatusPatch(
  rawStatus: string,
  prev: Application,
  extra: StatusExtra,
  actor: ActivityActor
): Database['public']['Tables']['applications']['Update'] {
  const status = normaliseApplicationStatus(rawStatus)
  const patch: Database['public']['Tables']['applications']['Update'] = {
    status,
    recruiter_feedback: extra.recruiterFeedback ?? prev.recruiter_feedback,
    interview_date: extra.interviewDate ?? prev.interview_date,
    package_lpa: extra.packageLpa ?? prev.package_lpa,
    notes: extra.notes ?? prev.notes,
  }

  if (isShortlistedDbStatus(status)) {
    patch.shortlisted_by = extra.shortlistedBy ?? actor.userName
    patch.shortlisted_at = new Date().toISOString()
  }
  if (status === 'forwarded') {
    patch.sent_to_recruiter_at = new Date().toISOString()
  }
  if (isPlacedDbStatus(status)) {
    patch.placed = true
  }

  return patch
}

/** Status-only patch for bulk updates — must not copy per-row fields from a single prev row. */
function buildBulkApplicationStatusPatch(
  rawStatus: string,
  actor: ActivityActor
): Database['public']['Tables']['applications']['Update'] {
  const status = normaliseApplicationStatus(rawStatus)
  const patch: Database['public']['Tables']['applications']['Update'] = { status }

  if (isShortlistedDbStatus(status)) {
    patch.shortlisted_by = actor.userName
    patch.shortlisted_at = new Date().toISOString()
  }
  if (status === 'forwarded') {
    patch.sent_to_recruiter_at = new Date().toISOString()
  }
  if (isPlacedDbStatus(status)) {
    patch.placed = true
  }

  return patch
}

async function updateCandidateCountersOnStatusChange(
  candidateId: string | null,
  prevStatus: string | null | undefined,
  newStatus: string,
  client: DbClient = defaultDbClient,
): Promise<void> {
  if (!candidateId) return

  const normalizedNew = normaliseApplicationStatus(newStatus)
  if (isShortlistedDbStatus(normalizedNew) && !isShortlistedDbStatus(prevStatus)) {
    const { error } = await client.rpc('increment_candidate_counter', {
      p_candidate_id: candidateId,
      p_counter: 'total_shortlists',
    })
    if (error) throw error
  }
  if (isPlacedDbStatus(normalizedNew) && !isPlacedDbStatus(prevStatus)) {
    const { error } = await client.rpc('increment_candidate_counter', {
      p_candidate_id: candidateId,
      p_counter: 'total_placements',
    })
    if (error) throw error
  }
}

/** Updates pipeline status and related timestamps; adjusts candidate counters; logs `UPDATE_APPLICATION_STATUS`. */
export async function updateApplicationStatus(
  id: string,
  status: string,
  extra: StatusExtra,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<Application> {
  const { data: prev, error: pe } = await client.from('applications').select('*').eq('id', id).is('deleted_at', null).maybeSingle()
  if (pe) throw pe
  if (!prev) throw new Error('Application not found or has been deleted')

  const patch = buildApplicationStatusPatch(status, prev, extra, actor)

  const { data, error } = await client.from('applications').update(patch).eq('id', id).select('*').single()
  if (error) throw error
  if (!data.status) throw new Error('Updated application is missing status')

  let candidateId: string | null = null
  if (prev.phone) {
    const { data: candidate } = await client.from('candidates').select('id').eq('phone', prev.phone).maybeSingle()
    candidateId = candidate?.id ?? null
  }
  await updateCandidateCountersOnStatusChange(candidateId, prev.status, data.status, client)

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_APPLICATION_STATUS',
      entityType: 'applications',
      entityId: id,
      oldValues: { status: prev.status },
      newValues: { status: data.status, placed: data.placed },
    }),
    client
  )

  // Fire-and-forget student notification
  void notifyStudentStatusChange(data.phone, data.job_id, prev.status, data.status, client)
  void sendPlacementEmail(id, data.status)

  return data
}

/** Creates shortlist row, marks applications `sent_to_recruiter`, returns recruiter email draft; logs `CREATE_SHORTLIST`. */
export async function createShortlist(
  payload: {
    jobId: string
    recruiterId: string
    candidatePhones: string[]
    notes?: string
  },
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<{ shortlist: Shortlist; emailDraft: string }> {
  const phones = [...new Set(payload.candidatePhones.map((p) => p.trim()).filter(Boolean))]
  if (!phones.length) throw new Error('At least one candidate phone is required')

  const job = await getJobById(payload.jobId, client)
  if (!job) throw new Error('Job not found')

  const { data: recruiter, error: re } = await client.from('recruiters').select('*').is('deleted_at', null).eq('id', payload.recruiterId).maybeSingle()
  if (re) throw re
  if (!recruiter) throw new Error('Recruiter not found')

  const { data: apps, error: ae } = await client
    .from('applications')
    .select('*')
    .eq('job_id', payload.jobId)
    .is('deleted_at', null)
    .in('phone', phones)
    .in('status', ['shortlisted', 'shortlisted_fintree'])
  if (ae) throw ae
  if (!apps?.length) throw new Error('No shortlisted applications found for the given phones')

  const { data: candidates, error: ce } = await client.from('candidates').select('*').in('phone', phones)
  if (ce) throw ce
  const candByPhone = new Map((candidates ?? []).map((c) => [c.phone, c]))

  const draftRows: ShortlistDraftRow[] = apps.map((app) => {
    const c = app.phone ? candByPhone.get(app.phone) : undefined
    const name = c?.name?.trim() || app.phone || 'Candidate'
    return {
      name,
      current_position: c?.current_position ?? null,
      experience_years: c?.experience_years ?? null,
      exp_band: c?.exp_band ?? null,
      education: c?.education ?? null,
      skills: c?.skills ?? null,
      linkedin_url: c?.linkedin_url ?? null,
      resume_url: c?.resume_url ?? null,
    }
  })

  const emailDraft = buildShortlistEmailDraft(job, { name: recruiter.name }, draftRows)

  const now = new Date().toISOString()
  const { data: shortlist, error: se } = await client
    .from('shortlists')
    .insert({
      job_id: payload.jobId,
      recruiter_id: payload.recruiterId,
      sent_by: actor.userName,
      sent_at: now,
      sent_via: 'email',
      candidate_count: apps.length,
      notes: payload.notes ?? null,
    })
    .select('*')
    .single()
  if (se) throw se

  const appIds = apps.map((a) => a.id)
  const { error: ue } = await client
    .from('applications')
    .update({ status: 'sent_to_recruiter', sent_to_recruiter_at: now })
    .in('id', appIds)
  if (ue) throw ue

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_SHORTLIST',
      entityType: 'shortlists',
      entityId: shortlist.id,
      newValues: { job_id: payload.jobId, recruiter_id: payload.recruiterId, count: apps.length },
    }),
    client
  )

  return { shortlist, emailDraft }
}

/** Bulk-update status for many applications at once; logs `BULK_UPDATE_APPLICATION_STATUS` per app. */
export async function bulkUpdateApplicationStatus(
  ids: string[],
  status: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<number> {
  if (!ids.length) return 0
  const uniqueIds = [...new Set(ids)]

  const { data: prevRows, error: pe } = await client
    .from('applications')
    .select('*')
    .is('deleted_at', null)
    .in('id', uniqueIds)
  if (pe) throw pe
  if (!prevRows?.length) return 0

  const normalizedStatus = normaliseApplicationStatus(status)
  const patch = buildBulkApplicationStatusPatch(status, actor)

  const { error } = await client.from('applications').update(patch).in('id', uniqueIds)
  if (error) throw error

  const candidatePhones = [...new Set(prevRows.map((r) => r.phone).filter((p): p is string => Boolean(p)))]
  const { data: candidates } = candidatePhones.length
    ? await client.from('candidates').select('id,phone').in('phone', candidatePhones)
    : { data: [] }
  const candidateIdByPhone = new Map((candidates ?? []).map((c) => [c.phone ?? '', c.id]))

  await Promise.all(
    prevRows.map((prev) =>
      updateCandidateCountersOnStatusChange(
        prev.phone ? candidateIdByPhone.get(prev.phone) ?? null : null,
        prev.status,
        normalizedStatus,
        client
      )
    )
  )

  await Promise.all(
    prevRows.map((prev) =>
      logActivity(
        withActivityActor(actor, {
          action: 'UPDATE_APPLICATION_STATUS',
          entityType: 'applications',
          entityId: prev.id,
          oldValues: { status: prev.status },
          newValues: { status: normalizedStatus },
        }),
        client
      )
    )
  )

  for (const prev of prevRows) {
    void notifyStudentStatusChange(prev.phone, prev.job_id, prev.status, normalizedStatus, client)
    void sendPlacementEmail(prev.id, normalizedStatus)
  }

  return uniqueIds.length
}

/** Withdraw an application by the student; logs `WITHDRAW_APPLICATION`. */
export async function withdrawApplication(
  id: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<Application> {
  const { data, error } = await client
    .from('applications')
    .update({ status: 'withdrawn', withdrawn_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'WITHDRAW_APPLICATION',
      entityType: 'applications',
      entityId: id,
      newValues: { status: 'withdrawn' },
    }),
    client
  )

  return data
}

/** Updates application notes only; logs `UPDATE_APPLICATION_NOTES`. */
export async function updateApplicationNotes(
  id: string,
  notes: string | null,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<Application> {
  const { data, error } = await client
    .from('applications')
    .update({ notes: notes ?? null })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_APPLICATION_NOTES',
      entityType: 'applications',
      entityId: id,
      newValues: { notes: notes ?? null },
    }),
    client
  )

  return data
}

/* ════════════════════════════════════════════════════════════════════
   Placements Inbox (writes)
   ════════════════════════════════════════════════════════════════════ */

/** `id` is the application id (inbox row key). */
export async function updateInboxStatus(
  applicationId: string,
  status: InboxStaffStatus,
  actor: ActivityActor,
  client: DbClient = defaultDbClient,
): Promise<void> {
  const row = {
    application_id: applicationId,
    staff_status: status,
    updated_at: new Date().toISOString(),
  }

  const { error } = await client.from('placements_inbox').upsert(row, { onConflict: 'application_id' })
  if (error) throw new ServiceError(error.message, 'PLACEMENTS_INBOX_UPDATE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'PLACEMENTS_INBOX_STATUS_UPDATE',
      entityType: 'applications',
      entityId: applicationId,
      newValues: { staff_status: status },
    }),
    client
  )
}

/* ════════════════════════════════════════════════════════════════════
   Shortlist package (async generators)
   ════════════════════════════════════════════════════════════════════ */

export async function generateResumeZip(candidates: ShortlistCandidate[]): Promise<ResumeZipResult> {
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  const failedUrls: ResumeZipResult['failedUrls'] = []

  for (const c of candidates) {
    if (!c.resumeUrl) {
      failedUrls.push({ name: c.name, url: '—', reason: 'No resume URL on file' })
      continue
    }
    try {
      const res = await fetch(c.resumeUrl)
      if (res.ok) {
        const blob = await res.blob()
        const safeName = c.name.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_') || 'candidate'
        const urlPath = new URL(c.resumeUrl).pathname
        const rawExt = urlPath.split('.').pop()?.toLowerCase() ?? ''
        const ext = ['pdf', 'doc', 'docx'].includes(rawExt) ? rawExt : 'pdf'
        zip.file(`${safeName}_resume.${ext}`, blob)
      } else {
        failedUrls.push({ name: c.name, url: c.resumeUrl, reason: `Download failed (${res.status})` })
      }
    } catch (_e) {
      failedUrls.push({ name: c.name, url: c.resumeUrl, reason: 'Download failed (network error)' })
    }
  }

  const blob = await zip.generateAsync({ type: 'blob' })
  return { blob, failedUrls }
}

export async function generateShortlistExcel(candidates: ShortlistCandidate[]): Promise<Blob> {
  const XLSX = await import('xlsx')
  const data = candidates.map((c) => ({
    Name: c.name,
    Phone: c.phone,
    Course: c.course,
    Experience: c.experience,
    Notes: c.notes ?? '',
    'Resume URL': c.resumeUrl ?? '',
  }))
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Shortlisted')
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

/* ════════════════════════════════════════════════════════════════════
   Placement History export
   ════════════════════════════════════════════════════════════════════ */

export async function generatePlacementHistoryExcel(rows: PlacementHistoryRow[]): Promise<Blob> {
  const XLSX = await import('xlsx')
  const data = rows.map((r) => ({
    'Student Name': r.studentName ?? '',
    'Company': r.companyName ?? '',
    'Role': r.role ?? '',
    'Salary': r.salary ?? '',
    'Placed Date': r.placedDate ?? '',
    Source: r.source ?? '',
  }))
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Placement History')
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}
